/**
 * ModelComponent — R3F component that loads and displays a GLB model.
 *
 * Responsibilities:
 * - Load GLB via AssetLoader (cache-first)
 * - Auto-center on bounding box (y = 0)
 * - Auto-scale to fit reference size
 * - Cast shadows on ground plane
 * - Emit 'sceneLoaded' when model is mounted and visible
 *
 * @see docs/ims-engine-api/01-architecture.md
 * @see docs/ims-engine-api/04-asset-pipeline.md
 */

import { useEffect, useLayoutEffect, useRef, useMemo, type ReactNode } from 'react';
import { useThree } from '@react-three/fiber';
import {
  Box3,
  Vector3,
  type Group,
  type Mesh,
  type Material,
  type Texture,
  type Object3D,
} from 'three';
import type { MeshStandardMaterial } from 'three';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ModelComponentProps {
  /** The loaded GLTF object (already loaded by AssetLoader). */
  gltf: GLTF;
  /** Target height for auto-scaling. Default: none (original size). */
  targetHeight?: number;
  /** Whether to auto-center the model on the XZ plane at y=0. Default: true */
  autoCenter?: boolean;
  /**
   * Vertical offset (metres) to sink the model below y=0 so that
   * tyres visually intersect the ground plane. Default: 0
   */
  groundSinkY?: number;
  /**
   * Called when the model is mounted and visible.
   * Receives the cloned scene that is actually rendered — use this
   * for MaterialManager.init() so mesh references are correct.
   */
  onMounted?: (clonedScene: import('three').Object3D) => void;
  /**
   * Optional content to render INSIDE the body model's group.
   * Use this for parentRelative objects (e.g. aftermarket rims) so they
   * automatically inherit the body's centering transform.
   */
  children?: ReactNode;
}

// ---------------------------------------------------------------------------
// Helper: compute centering offset and scale
// ---------------------------------------------------------------------------

export interface ModelTransform {
  /** Offset to move the model so its bounding box bottom sits at y=0. */
  offset: Vector3;
  /** Uniform scale factor (1 if no targetHeight). */
  scale: number;
}

/**
 * Compute centering offset and optional scale from a GLTF scene.
 * Pure function — no side effects. Easy to test.
 *
 * Accepts either a real Object3D (uses setFromObject for world-space accuracy)
 * or a mock with `traverse` for unit testing.
 */
// eslint-disable-next-line react-refresh/only-export-components -- utility function co-located with ModelComponent for cohesion; not a component module boundary
export function computeModelTransform(
  scene: { traverse?: (cb: (child: unknown) => void) => void } | Object3D,
  options: { autoCenter?: boolean; targetHeight?: number } = {},
): ModelTransform {
  const { autoCenter = true, targetHeight } = options;

  const box = new Box3();

  // Use Object3D.setFromObject when available (real Three.js scene) —
  // this correctly accounts for world transforms, giving accurate
  // bounding boxes regardless of parent group transforms.
  if ('isObject3D' in scene && (scene as Object3D).isObject3D) {
    (scene as Object3D).updateWorldMatrix(true, true);
    box.setFromObject(scene as Object3D);
  } else if (scene.traverse) {
    // Fallback for mocks in unit tests
    scene.traverse((child: unknown) => {
      const mesh = child as {
        geometry?: { boundingBox: Box3 | null; computeBoundingBox: () => void };
      };
      if (mesh.geometry) {
        if (!mesh.geometry.boundingBox) {
          mesh.geometry.computeBoundingBox();
        }
        if (mesh.geometry.boundingBox) {
          box.union(mesh.geometry.boundingBox);
        }
      }
    });
  }

  // Fallback if no geometry found
  if (box.isEmpty()) {
    return { offset: new Vector3(0, 0, 0), scale: 1 };
  }

  const size = new Vector3();
  const center = new Vector3();
  box.getSize(size);
  box.getCenter(center);

  // Centering offset: move center to origin, bottom to y=0
  const offset = autoCenter ? new Vector3(-center.x, -box.min.y, -center.z) : new Vector3(0, 0, 0);

  // Scale
  const scale = targetHeight && size.y > 0 ? targetHeight / size.y : 1;

  return { offset, scale };
}

// ---------------------------------------------------------------------------
// ModelComponent
// ---------------------------------------------------------------------------

export function ModelComponent({
  gltf,
  targetHeight,
  autoCenter = true,
  groundSinkY = 0,
  onMounted,
  children,
}: ModelComponentProps) {
  const groupRef = useRef<Group>(null);
  const invalidate = useThree((s) => s.invalidate);
  const gl = useThree((s) => s.gl);

  // Clone the scene so the original stays in cache for reuse
  const clonedScene = useMemo(() => gltf.scene.clone(true), [gltf]);

  // Enable shadows on all meshes, fix transparency sorting, set max anisotropy
  useEffect(() => {
    const maxAnisotropy = gl.capabilities.getMaxAnisotropy();

    clonedScene.traverse((child) => {
      if (!(child as { isMesh?: boolean }).isMesh) return;

      const meshObj = child as unknown as Mesh;
      meshObj.castShadow = true;

      // Normalise to material array (handles multi-material meshes)
      const mats: Material[] = Array.isArray(meshObj.material)
        ? meshObj.material
        : meshObj.material
          ? [meshObj.material]
          : [];

      for (const mat of mats) {
        const stdMat = mat as MeshStandardMaterial;

        // ── Anisotropic filtering ───────────────────────────────────────
        const textureMaps: (Texture | null | undefined)[] = [
          stdMat.map,
          stdMat.normalMap,
          stdMat.roughnessMap,
          stdMat.metalnessMap,
          stdMat.aoMap,
          stdMat.emissiveMap,
        ];
        for (const tex of textureMaps) {
          if (tex && tex.anisotropy !== maxAnisotropy) {
            tex.anisotropy = maxAnisotropy;
            tex.needsUpdate = true;
          }
        }
      }
    });
  }, [clonedScene, gl]);

  // Compute transform
  const transform = useMemo(
    () => computeModelTransform(clonedScene, { autoCenter, targetHeight }),
    [clonedScene, autoCenter, targetHeight],
  );

  // Stable ref for onMounted — avoids re-triggering the effect when the
  // parent passes a new arrow function reference each render.
  const onMountedRef = useRef(onMounted);
  useLayoutEffect(() => {
    onMountedRef.current = onMounted;
  });

  // Notify when mounted — pass clonedScene so caller gets the rendered object refs
  useEffect(() => {
    onMountedRef.current?.(clonedScene);
    invalidate();
  }, [invalidate, clonedScene]);

  return (
    <group ref={groupRef} scale={transform.scale}>
      {/* Inner group applies auto-center offset + groundSinkY.
          Both the body primitive AND children (e.g. body-scope ObjectComponent
          for aftermarket rims) share this transform, so external GLBs authored
          in the same coordinate space as the body align correctly. */}
      <group position={[transform.offset.x, transform.offset.y - groundSinkY, transform.offset.z]}>
        <primitive object={clonedScene} />
        {children}
      </group>
    </group>
  );
}
